import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { LoadingSpinner } from '@/components/auth'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  LanguageSwitcher,
} from '@/components/ui'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/contexts/auth'
import { captureError } from '@/lib/monitoring'
import { supabase } from '@/lib/supabase'

export function ResetPasswordPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    // Check if user came from email reset link
    const fromEmail = searchParams.get('from') === 'email'

    // If user is already authenticated and not coming from email reset, redirect to dashboard
    if (user && !fromEmail) {
      const isPasswordResetFlow = sessionStorage.getItem('password-reset-flow') === 'true'
      if (!isPasswordResetFlow) {
        navigate('/dashboard', { replace: true })
      }
    }

    // If coming from email, set the password reset flow flag
    if (fromEmail) {
      sessionStorage.setItem('password-reset-flow', 'true')
    }
  }, [user, navigate, searchParams])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!password) {
      newErrors.password = t('auth.passwordRequired')
    } else if (password.length < 6) {
      newErrors.password = t('auth.passwordTooShort')
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordRequired')
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('auth.passwordsDoNotMatch')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsLoading(true)
    setError(null)

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      })

      if (error) throw error

      setSuccess(true)

      // Clear the password reset flow flag
      sessionStorage.removeItem('password-reset-flow')

      // After successful password update, redirect to login
      setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
    } catch (error) {
      captureError(error as Error, { context: 'resetPassword' })
      setError(t('auth.generalError'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string) => {
    if (field === 'password') {
      setPassword(value)
    } else {
      setConfirmPassword(value)
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }))
    }
  }

  if (success) {
    return (
      <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-background">
        {/* Decorative elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

        <div className="relative z-10 w-full max-w-md px-4">
          <Card className="border-0 shadow-xl bg-card/95 backdrop-blur">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="w-12 h-12 mx-auto bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center">
                  <svg
                    className="w-6 h-6 text-green-600 dark:text-green-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-foreground">
                  {t('auth.passwordUpdated')}
                </h2>
                <p className="text-sm text-muted-foreground">{t('auth.passwordUpdateSuccess')}</p>
                <p className="text-xs text-muted-foreground">{t('auth.redirectingToLogin')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  return (
    <div className="relative flex items-center justify-center min-h-screen overflow-hidden bg-background">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 w-full max-w-md px-4">
        <Card className="border-0 shadow-xl bg-card/95 backdrop-blur">
          <CardHeader className="space-y-4 pb-6">
            <div className="flex justify-center">
              <div className="w-24 h-24 flex items-center justify-center">
                <img src="/logo.svg" alt="Longbourn Club" className="w-20 h-20 object-contain" />
              </div>
            </div>
            <div className="space-y-2 text-center">
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-primary to-purple bg-clip-text text-transparent">
                {t('auth.setNewPassword')}
              </CardTitle>
              <CardDescription className="text-base">{t('auth.enterNewPassword')}</CardDescription>
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {error && (
              <div className="p-4 bg-error/5 border border-error/20 rounded-lg">
                <p className="text-sm text-error flex items-center gap-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                    />
                  </svg>
                  {error}
                </p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  {t('auth.newPassword')}
                </label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  className={errors.password ? 'border-error' : ''}
                  disabled={isLoading}
                  placeholder={t('auth.passwordPlaceholder')}
                />
                {errors.password && <p className="text-error text-sm mt-1">{errors.password}</p>}
              </div>

              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-foreground mb-1"
                >
                  {t('auth.confirmNewPassword')}
                </label>
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                  className={errors.confirmPassword ? 'border-error' : ''}
                  disabled={isLoading}
                  placeholder={t('auth.confirmPasswordPlaceholder')}
                />
                {errors.confirmPassword && (
                  <p className="text-error text-sm mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              <Button type="submit" disabled={isLoading} size="lg" className="w-full">
                {isLoading ? <LoadingSpinner size="sm" /> : t('auth.updatePassword')}
              </Button>
            </form>

            <div className="text-center text-sm text-muted-foreground pt-4">
              <button
                type="button"
                onClick={() => {
                  sessionStorage.removeItem('password-reset-flow')
                  navigate('/login')
                }}
                className="text-primary hover:underline transition-colors"
                disabled={isLoading}
              >
                {t('auth.backToSignIn')}
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Language Switcher - centered below card */}
        <div className="flex justify-center mt-6">
          <LanguageSwitcher variant="minimal" />
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">{t('auth.madeWithLove')}</p>
      </div>
    </div>
  )
}
